/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { motion } from "framer-motion";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import EventToolsBackButton from "./EventToolsBackButton";
import EventToolsSkeleton from "./EventToolsSkeletons";

const EVENT_TOOLS_LIFECYCLE_ACTIONS = new Set(["boost_event", "delete_event"]);
const EVENT_TOOLS_ACTIVE_MIN_HEIGHT = "min(68vh, 600px)";
const eventToolSkeletonVariantByActionId = {
  view_waitlist: "waitlist",
  add_co_host: "role",
  transfer_hosting: "role",
  send_intention_request: "intention",
  remove_participant: "participants",
  open_analytics: "analytics",
  concept_date_poll: "datePoll",
  review_participant_requests: "requests",
  boost_event: "boost",
  delete_event: "delete",
};

export const isPaidEventHostTransferUnavailable = (event) => {
  if (typeof event?.host_transfer_pricing_allowed === "boolean") {
    return !event.host_transfer_pricing_allowed;
  }
  const pricingModel = String(event?.pricing_model || "").trim().toLowerCase();
  if (pricingModel === "shared_cost") {
    return (
      String(event?.ticketing_flow_kind || "").trim().toLowerCase() === "host_stripe" ||
      Boolean(String(event?.host_payment_collection_method || "").trim())
    );
  }
  if (pricingModel && pricingModel !== "free") return true;

  const feeTypeText = String(event?.fee_type ?? "").trim().toLowerCase();
  if (feeTypeText === "paid") return true;
  const numericFeeType = Number(feeTypeText || 0);
  return Number.isFinite(numericFeeType) && numericFeeType !== 0;
};

export const usesHostPaymentRequest = (event) =>
  event?.pricing_model === "host_paid" &&
  event?.ticketing_flow_kind === "host_stripe" &&
  event?.host_payment_collection_method === "card" &&
  event?.host_paid_balance_due_days_before != null &&
  Number(event?.host_paid_join_payment_percent ?? 0) < 100;

const HostMoreActionsPanel = ({
  open = false,
  t,
  isPrivateEvent = false,
  isConcept = false,
  paymentRequest = false,
  hasConceptDatePoll = false,
  waitlistUnreadCount = 0,
  participantRequestsPendingCount = 0,
  onBack = () => {},
  onActionClick = null,
  keepOpenActionIds = [],
  onPrepareAction = null,
  renderActionView = null,
  showLifecycleActions = true,
  eventBoosted = false,
  hostTransferUnavailable = false,
}) => {
  const [activeActionId, setActiveActionId] = React.useState(null);
  const [preparingActionId, setPreparingActionId] = React.useState(null);
  const actionRequestSeqRef = React.useRef(0);

  const keepOpenActionIdSet = React.useMemo(
    () => new Set(Array.isArray(keepOpenActionIds) ? keepOpenActionIds : []),
    [keepOpenActionIds]
  );

  React.useEffect(() => {
    if (open) return;
    setActiveActionId(null);
    setPreparingActionId(null);
  }, [open]);

  const renderUnreadBadge = (count) => {
    const n = Number(count) || 0;
    if (n <= 0) return null;
    const label = n > 999 ? "999+" : String(n);
    return (
      <span
        className="pointer-events-none absolute right-2 top-1.5 z-20 bg-white text-blue-600 font-extrabold leading-none h-[18px] min-w-[18px] px-[5px] rounded-full ring-2 ring-blue-500 text-[11px] flex items-center justify-center shadow-[0_0_8px_rgba(255,255,255,0.45)]"
        aria-hidden="true"
      >
        {label}
      </span>
    );
  };

  const closePanel = React.useCallback(() => {
    actionRequestSeqRef.current += 1;
    setActiveActionId(null);
    setPreparingActionId(null);
    onBack();
  }, [onBack]);

  const backToTools = React.useCallback(() => {
    actionRequestSeqRef.current += 1;
    setActiveActionId(null);
    setPreparingActionId(null);
  }, []);

  const handleAction = async (actionId) => {
    if (actionId === "boost_event" && eventBoosted) return;

    if (actionId === "transfer_hosting" && hostTransferUnavailable) {
      actionRequestSeqRef.current += 1;
      setPreparingActionId(null);
      setActiveActionId(actionId);
      return;
    }

    const canRenderInlineAction = typeof renderActionView === "function";

    if (EVENT_TOOLS_LIFECYCLE_ACTIONS.has(actionId) && !canRenderInlineAction) {
      if (!keepOpenActionIdSet.has(actionId)) {
        closePanel();
      }
      if (typeof onActionClick === "function") {
        onActionClick(actionId);
        return;
      }
    }

    if (canRenderInlineAction) {
      const requestSeq = actionRequestSeqRef.current + 1;
      actionRequestSeqRef.current = requestSeq;
      setActiveActionId(actionId);
      setPreparingActionId(actionId);
      try {
        if (typeof onPrepareAction === "function") {
          const prepared = await onPrepareAction(actionId);
          if (actionRequestSeqRef.current !== requestSeq) return;
          if (prepared === false) {
            setActiveActionId(null);
            return;
          }
        }
        return;
      } finally {
        if (actionRequestSeqRef.current === requestSeq) {
          setPreparingActionId(null);
        }
      }
    }

    if (typeof onActionClick === "function") {
      if (!keepOpenActionIdSet.has(actionId)) {
        closePanel();
      }
      onActionClick(actionId);
      return;
    }

    window.dispatchEvent(
      new CustomEvent("global-info", {
        detail: {
          message: t("events.details.more_actions.action_coming_soon"),
        },
      })
    );
  };

  const baseConceptActions = [
    {
      id: "concept_date_poll",
      label: hasConceptDatePoll
        ? t("events.details.more_actions.edit_available_dates")
        : t("events.details.more_actions.send_available_dates"),
      iconSrc: "/assets/events/intention_send.svg",
    },
    ...(isPrivateEvent
      ? [
          {
            id: "review_participant_requests",
            label: t("events.details.more_actions.review_participant_requests"),
            iconSrc: "/assets/events/requests_view.svg",
            unreadCount: participantRequestsPendingCount,
          },
        ]
      : []),
    {
      id: "remove_participant",
      label: t("events.details.more_actions.remove_participant"),
      iconSrc: "/assets/events/delete_participant.svg",
    },
    {
      id: "view_waitlist",
      label: t("events.details.more_actions.view_waitlist"),
      iconSrc: "/assets/events/waitlist_view.svg",
      unreadCount: waitlistUnreadCount,
    },
    {
      id: "add_co_host",
      label: t("events.details.more_actions.add_remove_co_host"),
      iconSrc: "/assets/events/add_cohosts.svg",
    },
    {
      id: "transfer_hosting",
      label: t("events.details.more_actions.transfer_hosting"),
      iconSrc: "/assets/events/add_cohosts.svg",
    },
    {
      id: "open_analytics",
      label: t("events.details.more_actions.analytics"),
      iconSrc: "/assets/events/analytics.svg",
    },
  ];

  const baseEventActions = [
    {
      id: "send_intention_request",
      label: t(paymentRequest ? "events.details.more_actions.send_payment_request" : "events.details.more_actions.send_intention_request"),
      iconSrc: "/assets/events/intention_send.svg",
    },
    ...(isPrivateEvent
      ? [
          {
            id: "review_participant_requests",
            label: t("events.details.more_actions.review_participant_requests"),
            iconSrc: "/assets/events/requests_view.svg",
            unreadCount: participantRequestsPendingCount,
          },
        ]
      : []),
    {
      id: "remove_participant",
      label: t("events.details.more_actions.remove_participant"),
      iconSrc: "/assets/events/delete_participant.svg",
    },
    {
      id: "view_waitlist",
      label: t("events.details.more_actions.view_waitlist"),
      iconSrc: "/assets/events/waitlist_view.svg",
      unreadCount: waitlistUnreadCount,
    },
    {
      id: "add_co_host",
      label: t("events.details.more_actions.add_remove_co_host"),
      iconSrc: "/assets/events/add_cohosts.svg",
    },
    {
      id: "transfer_hosting",
      label: t("events.details.more_actions.transfer_hosting"),
      iconSrc: "/assets/events/add_cohosts.svg",
    },
    {
      id: "open_analytics",
      label: t("events.details.more_actions.analytics"),
      iconSrc: "/assets/events/analytics.svg",
    },
  ];

  const actions = isConcept ? baseConceptActions : baseEventActions;
  const paidTransferUnavailableView =
    activeActionId === "transfer_hosting" && hostTransferUnavailable
      ? {
          title: t("events.details.host_role_modal.transfer_title"),
          maxWidthClassName: "max-w-[640px]",
          desktopMaxHeight: "min(72vh, 560px)",
          minHeight: "min(40vh, 360px)",
          body: (
            <div className="flex min-h-[240px] flex-col items-center justify-center px-3 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/12 bg-[#071024]/70">
                <img
                  src="/assets/events/add_cohosts.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-8 w-8 brightness-0 invert"
                />
              </span>
              <p className="mt-5 max-w-[440px] text-[16px] font-semibold leading-6 text-white/88">
                {t("events.details.host_role_modal.paid_transfer_unavailable")}
              </p>
            </div>
          ),
        }
      : null;
  const activeActionView = paidTransferUnavailableView || (
    activeActionId && typeof renderActionView === "function"
      ? renderActionView(activeActionId, {
          back: backToTools,
          close: closePanel,
          setActiveActionId,
        })
      : null
  );
  const hasActiveActionView = Boolean(activeActionId && activeActionView);
  const isActiveActionPreparing = Boolean(
    activeActionId && preparingActionId === activeActionId
  );
  const activeSkeletonVariant =
    eventToolSkeletonVariantByActionId[activeActionId] || "default";
  const activeMinHeight =
    hasActiveActionView && activeActionView.minHeight !== undefined
      ? activeActionView.minHeight
      : hasActiveActionView
      ? EVENT_TOOLS_ACTIVE_MIN_HEIGHT
      : null;

  const boostLabel = eventBoosted
    ? t("events.details.boosted_button")
    : t(
        isConcept
          ? "events.details.boost_concept_button"
          : "events.details.boost_button"
      );
  const deleteLabel = t(
    isConcept
      ? "events.details.delete_concept_button"
      : "events.details.delete_button"
  );

  return (
    <AppModalShell
      open={open}
      title={
        hasActiveActionView
          ? activeActionView.title || t("events.details.more_actions.title", { defaultValue: "Event tools" })
          : t("events.details.more_actions.title", { defaultValue: "Event tools" })
      }
      description={hasActiveActionView ? activeActionView.description || "" : ""}
      onClose={closePanel}
      closeAriaLabel={t("event_description_modal.header.close_aria", {
        defaultValue: "Close",
      })}
      maxWidthClassName={
        hasActiveActionView
          ? activeActionView.maxWidthClassName || "max-w-[640px]"
          : "max-w-[640px]"
      }
      desktopMaxHeight={
        hasActiveActionView
          ? activeActionView.desktopMaxHeight || "min(86vh, 740px)"
          : "min(82vh, 720px)"
      }
      panelClassName="border-white/28"
      titleClassName={
        hasActiveActionView
          ? `!text-[24px] sm:!text-[24px] !leading-[28px] !tracking-normal ${activeActionView.titleClassName || ""}`
          : "!text-[24px] sm:!text-[24px] !leading-[28px] !tracking-normal"
      }
      headerClassName={
        hasActiveActionView
          ? `px-5 pb-2 pt-5 ${activeActionView.headerClassName || ""}`
          : "px-5 pb-2 pt-5"
      }
      bodyClassName={
        hasActiveActionView
          ? `px-5 pb-5 pt-1 ${
              activeActionView.pinnedFooter ? "!flex !min-h-0 !flex-col !overflow-hidden" : ""
            } ${activeActionView.bodyClassName || ""}`
          : "px-5 pb-5 pt-1"
      }
      zIndexClassName="z-[13000]"
      layout
      panelStyle={
        activeMinHeight
          ? { minHeight: activeMinHeight }
          : { minHeight: 0 }
      }
      leftAction={
        hasActiveActionView ? (
          <EventToolsBackButton
            onClick={backToTools}
            label={t("events.details.more_actions.back")}
            disabled={Boolean(activeActionView.backDisabled)}
          />
        ) : null
      }
      footer={hasActiveActionView ? activeActionView.footer || null : null}
    >
      <motion.div
        key={
          hasActiveActionView
            ? `event-tool-${activeActionId}-${isActiveActionPreparing ? "skeleton" : "content"}`
            : "event-tools-menu"
        }
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        className={
          hasActiveActionView && activeActionView.pinnedFooter
            ? "flex min-h-0 flex-1 flex-col"
            : ""
        }
      >
        {hasActiveActionView ? (
          <>
            {isActiveActionPreparing ? (
              <EventToolsSkeleton variant={activeSkeletonVariant} />
            ) : (
              activeActionView.body || activeActionView.content || null
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
              {actions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleAction(action.id);
                  }}
                  disabled={Boolean(preparingActionId)}
                  className="group relative flex min-h-[112px] w-full flex-col justify-end overflow-hidden rounded-[24px] border border-white/[0.06] bg-black/45 px-4 pb-4 pt-5 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:bg-black/55 active:scale-[0.985] disabled:cursor-wait disabled:opacity-60 sm:min-h-[120px]"
                >
                  {renderUnreadBadge(action.unreadCount)}
                  {action.iconSrc ? (
                    <span className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-[#071024]/65">
                      <img
                        src={action.iconSrc}
                        alt=""
                        className="h-[21px] w-[21px] shrink-0 brightness-0 invert"
                      />
                    </span>
                  ) : null}
                  <span className="max-w-[190px] break-words text-[16px] font-extrabold leading-[20px] tracking-normal">
                    {action.label}
                  </span>
                </button>
              ))}
            </div>

            {showLifecycleActions ? (
              <div className="mt-7 grid grid-cols-1 gap-3">
                <AppModalButton
                  variant="primary"
                  onClick={() => void handleAction("boost_event")}
                  disabled={eventBoosted || Boolean(preparingActionId)}
                  initial={false}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  className={`${
                    eventBoosted
                      ? "!bg-emerald-500 !text-white"
                      : "!border !border-white/35 !bg-white !text-[#3e6ffb] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_14px_34px_rgba(255,255,255,0.08)] disabled:!bg-white/20 disabled:!text-white/60"
                  }`}
                >
                  {boostLabel}
                </AppModalButton>
                <AppModalButton
                  variant="danger"
                  onClick={() => void handleAction("delete_event")}
                  disabled={Boolean(preparingActionId)}
                  initial={false}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  className="shadow-[0_12px_30px_rgba(239,68,88,0.26)]"
                >
                  {deleteLabel}
                </AppModalButton>
              </div>
            ) : null}
          </>
        )}
      </motion.div>
    </AppModalShell>
  );
};

export default HostMoreActionsPanel;
